// Composes the `Age · Gender · Location` line for an agent-sourced creator row.
// Sourced creators can have incomplete data (BRAND-9330); the separator must
// only appear between parts that are actually present, and the line should be
// empty when nothing is available so the caller can omit it entirely.

import { ISourcingRun } from '../types'

const isPresent = (value?: string | null): value is string =>
  value !== null && value !== undefined && value !== ''

const genderLetter = (gender?: string | null): string =>
  gender === 'MALE' ? 'M' : gender === 'FEMALE' ? 'F' : ''

/**
 *   age + gender + country -> "35-44 M · GB"
 *   age + gender           -> "35-44 M"
 *   country only           -> "GB"        (no leading "· ")
 *   gender + country       -> "F · GB"
 *   nothing                -> ""
 */
export const formatProfileMeta = (
  ageGroup?: string | null,
  gender?: string | null,
  countryCode?: string | null,
): string => {
  const ageGender = [ageGroup, genderLetter(gender)].filter(isPresent).join(' ')
  return [ageGender, countryCode].filter(isPresent).join(' · ')
}

/**
 * Highest ordinal among the given runs (undefined when there are none) —
 * unconditional recency, regardless of status.
 *
 * BRAND-9515/9550 fix note: this stays unconditional on purpose. The default
 * *review-list selection* (RunTabs' highlighted tab / record bar and
 * AgentSourcedCreatorListView's RUNNING/FAILED gating, both resolved via
 * `effectiveSelectedRun.getEffectiveSelectedRun`) was changed to prefer the
 * newest run whose import COMPLETED, then the newest non-failed one,
 * mirroring the BE's `default_run_ids` resolution.
 *
 * BRAND-9589 note: this used to also drive a per-row "NEW" badge. That badge
 * was removed (it leaked onto the List tab, where rows have no run-of-origin
 * context, and read as a status signal rather than the run artefact it was);
 * `getEffectiveSelectedRun` is now the only consumer.
 */
export const getLatestRunOrdinal = (runs: ISourcingRun[] | undefined): number | undefined => {
  if (!runs || runs.length === 0) return undefined
  return runs.reduce((max, run) => Math.max(max, run.ordinal), runs[0].ordinal)
}
